import type { CalendarEvent } from '@repo/common/types/Calendar';
import type { UICalendarEvent } from '@repo/common/types/UICalendar';
import type { Section, SectionWithMetadata, SectionConfig } from '@repo/common/types/FilterSet';
import { addDays, format, isTomorrow, startOfDay } from 'date-fns';
import type { Readable } from 'svelte/store';
import { FilteredType } from './base';
import type { UnionIconValues } from '@repo/common';

// UI-friendly calendar event type for display
export interface CalendarEventItemType {
  id: string;
  title: string;
  time: string;
  fullDate: string;
  category: string;
  categoryIcon: UnionIconValues;
  timeRemaining?: string;
  location?: string;
  isFavorite?: boolean;
  event: CalendarEvent;
  uiEvent?: UICalendarEvent; // Include reference to UICalendarEvent
}

// Concrete implementation for Calendar Events working with CalendarEvents
export class FilteredCalendarEvents extends FilteredType<UICalendarEvent, CalendarEventItemType> {
  // Cache for sorted items to avoid re-sorting when data hasn't changed
  private lastItems: UICalendarEvent[] | null = null;
  private lastSortedItems: UICalendarEvent[] | null = null;
  private lastItemsLength: number = 0;

  constructor(items: Readable<UICalendarEvent[]>, initialLimit: number = 50) {
    super(items, initialLimit);

    // Initialize section states with defaults
    this.sectionStates.set(new Map([
      ['today', { collapsed: false, displayLimit: 10 }],
      ['tomorrow', { collapsed: false, displayLimit: 10 }],
      ['upcoming', { collapsed: false, displayLimit: 10 }]
    ]));
  }

  protected sortItems(items: UICalendarEvent[]): UICalendarEvent[] {
    // Check if we can use cached sorted items (reference equality and length check)
    if (this.lastItems === items && this.lastSortedItems && this.lastItemsLength === items.length) {
      return this.lastSortedItems;
    }

    // Pre-compute timestamps for efficient sorting
    const itemsWithTimestamps = items.map((item) => ({
      item,
      timestamp: item.start.getTime()
    }));

    // Sort using pre-computed timestamps (much faster than repeated Date operations)
    const sortedWithTimestamps = itemsWithTimestamps.sort((a, b) => a.timestamp - b.timestamp);
    const sorted = sortedWithTimestamps.map(({ item }) => item);

    // Cache the result
    this.lastItems = items;
    this.lastSortedItems = sorted;
    this.lastItemsLength = items.length;

    return sorted;
  }

  protected mapItem(uiEvent: UICalendarEvent): CalendarEventItemType {
    const startDate = uiEvent.start;
    const date = startOfDay(startDate);

    return {
      id: uiEvent.id,
      title: uiEvent.title,
      time: `${format(date, 'EEEE')}, ${this.formatEventTime(startDate)}`,
      fullDate: date.getTime().toString(), // Use timestamp instead of ISO string to avoid timezone issues
      category: uiEvent.calendarId || 'calendar',
      categoryIcon: 'calendar',
      timeRemaining: uiEvent.timeRemaining,
      location: uiEvent.location || undefined,
      isFavorite: uiEvent.isFavorite || uiEvent.originalEvent?.isFavorite,
      event: uiEvent.originalEvent, // Convert back to CalendarEvent for compatibility
      uiEvent // Keep reference to UICalendarEvent
    };
  }

  protected generateSections(items: CalendarEventItemType[]): Section<CalendarEventItemType>[] {
    // Pre-compute date boundaries for efficient comparison
    const today = startOfDay(new Date());
    const tomorrow = addDays(today, 1);
    const todayTime = today.getTime();
    const tomorrowTime = tomorrow.getTime();

    const sections = [
      { title: 'Today', items: [] as CalendarEventItemType[] },
      { title: 'Tomorrow', items: [] as CalendarEventItemType[] },
      { title: 'Upcoming', items: [] as CalendarEventItemType[] }
    ];

    for (const item of items) {
      const dateTime = parseInt(item.fullDate);

      if (dateTime === todayTime) {
        sections[0].items.push(item);
      } else if (dateTime === tomorrowTime) {
        sections[1].items.push(item);
      } else if (dateTime > tomorrowTime) {
        sections[2].items.push(item);
      } else {
        console.warn('Calendar event not in any section:', item);
      }
      // Past events are filtered out by default in applyFilters
    }

    return sections.filter((section) => section.items.length > 0);
  }

  // Override to provide section metadata
  protected generateSectionsWithMetadata(items: UICalendarEvent[]): SectionWithMetadata<UICalendarEvent>[] {
    const sections = this.generateSectionsFromRawItems(items);

    const metadata: Record<string, SectionConfig> = {
      'Today': {
        id: 'today',
        icon: 'calendar' as any,
        iconColor: 'text-blue-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Tomorrow': {
        id: 'tomorrow',
        icon: 'calendar' as any,
        iconColor: 'text-green-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      },
      'Upcoming': {
        id: 'upcoming',
        icon: 'calendar' as any,
        iconColor: 'text-purple-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 10
      }
    };

    return sections.map(section => ({
      ...section,
      config: metadata[section.title]
    }));
  }

  // New method for lazy mapping optimization - section raw items before mapping
  public generateSectionsFromRawItems(items: UICalendarEvent[]): Section<UICalendarEvent>[] {
    // Pre-compute date boundaries for efficient comparison
    const today = startOfDay(new Date());
    const tomorrow = addDays(today, 1);
    const todayTime = today.getTime();
    const tomorrowTime = tomorrow.getTime();

    const sections = [
      { title: 'Today', items: [] as UICalendarEvent[] },
      { title: 'Tomorrow', items: [] as UICalendarEvent[] },
      { title: 'Upcoming', items: [] as UICalendarEvent[] }
    ];

    for (const item of items) {
      const dateTime = startOfDay(item.start).getTime();

      if (dateTime === todayTime) {
        sections[0].items.push(item);
      } else if (dateTime === tomorrowTime) {
        sections[1].items.push(item);
      } else if (dateTime > tomorrowTime) {
        sections[2].items.push(item);
      } else {
        console.warn('Calendar event not in any section:', item);
      }
      // Past events are filtered out by default in applyFilters
    }

    return sections.filter((section) => section.items.length > 0);
  }

  // Override helper methods to extract data from UICalendarEvent items
  protected getItemTags(item: UICalendarEvent): string[] {
    // UICalendarEvent has pre-computed tags
    return item.tags || [];
  }

  protected getItemSpace(item: UICalendarEvent): string | undefined {
    // UICalendarEvent has pre-computed workspace info
    return item.workspaceId;
  }

  protected getItemText(item: UICalendarEvent): string {
    // Extract searchable text from UICalendarEvent
    const title = item.title || '';
    const description = item.description || '';
    const location = item.location || '';
    const tags = item.tags ? item.tags.join(' ') : '';
    const attendees = item.originalEvent?.attendees?.map((a) => a.name || a.email).join(' ') || '';

    return `${title} ${description} ${location} ${tags} ${attendees}`.trim();
  }

  protected getItemCalendar(item: UICalendarEvent): string | undefined {
    // Extract calendar from UICalendarEvent
    return item.calendarId;
  }

  protected getItemMemory(item: UICalendarEvent): string | undefined {
    // UICalendarEvent has pre-computed transcript info
    return item.transcriptId;
  }

  protected isItemCompleted(item: UICalendarEvent): boolean {
    // UICalendarEvent has pre-computed status flags
    return item.isPast;
  }

  protected matchesDatePreset(item: UICalendarEvent, datePresetValues: string[]): boolean {
    // Use pre-computed flags from UICalendarEvent when available for better performance
    return datePresetValues.some((preset) => {
      switch (preset) {
        case 'future':
          return item.isUpcoming;
        case 'today':
          return item.isToday;
        case 'tomorrow':
          return isTomorrow(item.start);
        case 'this-week': {
          const today = startOfDay(new Date());
          const endOfWeek = addDays(today, 7);
          return item.start >= today && item.start <= endOfWeek;
        }
        case 'this-month': {
          const today = startOfDay(new Date());
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          return item.start >= today && item.start <= endOfMonth;
        }
        case 'past-90-days': {
          const today = startOfDay(new Date());
          const past90Days = addDays(today, -90);
          return item.start >= past90Days && item.start < today;
        }
        default:
          return true;
      }
    });
  }

  protected calculateDatePresetCount(datePresetValue: string, items: UICalendarEvent[]): number {
    return items.filter((item) => this.matchesDatePreset(item, [datePresetValue])).length;
  }

  // Helper methods for formatting
  private formatEventTime(date: Date): string {
    return format(date, 'h:mm a');
  }
}
